import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Circle, Video } from 'lucide-react';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { toast } from 'react-hot-toast';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const MeetingRecorder = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [title, setTitle] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const socketRef = useRef(null);
  const deepgramRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [speakerCount, setSpeakerCount] = useState(0);
  const [diarizationStatus, setDiarizationStatus] = useState('waiting'); // 'waiting', 'detecting', 'active'
  const [transcriptSegments, setTranscriptSegments] = useState([]); // Store structured transcript data
  const [interimSegment, setInterimSegment] = useState(null); // Store current interim result

  // Helper function to get speaker color
  const getSpeakerColor = (speaker) => {
    const lightColors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800',
    ];
    
    const darkColors = [
      'bg-blue-900/30 text-blue-300',
      'bg-green-900/30 text-green-300',
      'bg-purple-900/30 text-purple-300',
      'bg-orange-900/30 text-orange-300',
      'bg-pink-900/30 text-pink-300',
      'bg-indigo-900/30 text-indigo-300',
    ];
    
    const colors = theme === 'dark' ? darkColors : lightColors;
    return colors[speaker % colors.length];
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Initialize Deepgram client when component mounts
  useEffect(() => {
    deepgramRef.current = createClient(process.env.REACT_APP_DEEPGRAM_API_KEY);
    
    return () => {
      if (socketRef.current) {
        socketRef.current.finish();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleProcessTranscript = async (transcript, meetingId) => {
    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/process-meeting-transcript',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transcript,
            meetingId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to process transcript');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error processing transcript:', error);
      toast.error('Failed to process transcript');
      throw error;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!title.trim()) {
      toast.error('Please enter a meeting title before uploading');
      return;
    }

    try {
      setIsUploading(true);
      toast.loading('Uploading audio file...');

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('audio', file);

      // Upload audio file
      const uploadResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
        {
          method: 'POST',
          headers: {},
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload audio file');
      }

      const { audioUrl } = await uploadResponse.json();

      // Transcribe audio
      const transcribeResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/transcribe',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ audioUrl })
        }
      );

      if (!transcribeResponse.ok) {
        throw new Error('Failed to transcribe audio');
      }

      const { transcript } = await transcribeResponse.json();

      // Save meeting with raw transcript
      const saveMeetingResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: title || 'Untitled Meeting',
            date: new Date().toISOString(),
            transcript,
            audioUrl
          })
        }
      );

      if (!saveMeetingResponse.ok) {
        throw new Error('Failed to save meeting');
      }

      const meeting = await saveMeetingResponse.json();

      // Process the transcript
      await handleProcessTranscript(transcript, meeting.id);

      toast.dismiss();
      toast.success('Meeting uploaded and processed successfully');
      navigate(`/meetings/${meeting.id}`);
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.dismiss();
      toast.error('Failed to process meeting recording');
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = async () => {
    if (!title.trim()) {
      toast.error('Please enter a meeting title before recording');
      return;
    }

    try {
      if (!deepgramRef.current) {
        throw new Error('Deepgram client not initialized');
      }

      setTranscript('');
      setTranscriptSegments([]);
      setInterimSegment(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
          if (socketRef.current) {
            socketRef.current.send(event.data);
          }
        }
      });

      mediaRecorderRef.current.addEventListener('stop', async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const file = new File([audioBlob], `meeting-${Date.now()}.webm`, { type: 'audio/webm' });
        setAudioFile(file);
        try {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('audio', file);

          // Upload audio file
          const uploadResponse = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
            {
              method: 'POST',
              body: formData
            }
          );

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload audio file');
          }

          const { audioUrl } = await uploadResponse.json();
          setAudioUrl(audioUrl);

          // Get the final transcript
          const finalTranscript = transcript;

          // Save meeting with raw transcript
          const saveMeetingResponse = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                title: title || 'Untitled Meeting',
                date: new Date().toISOString(),
                transcript: finalTranscript,
                audioUrl
              })
            }
          );

          if (!saveMeetingResponse.ok) {
            throw new Error('Failed to save meeting');
          }

          const meeting = await saveMeetingResponse.json();

          // Process the transcript
          console.log('Processing transcript:', finalTranscript);
          const processedResult = await handleProcessTranscript(finalTranscript, meeting.id);
          console.log('Processed result:', processedResult);

          toast.success('Meeting recorded and processed successfully');
          navigate(`/meetings/${meeting.id}`);
        } catch (error) {
          console.error('Error uploading audio:', error);
          toast.error('Failed to process meeting recording');
        }
      });

      const connection = deepgramRef.current.listen.live({
        language: 'en-US',
        smart_format: true,
        model: 'nova-3',
        interim_results: true,   // Enable interim results for real-time feedback
        diarize: true,           // Enable speaker diarization to identify different speakers
        punctuate: true,         // Add punctuation and capitalization
        paragraphs: true,        // Split into paragraphs for better readability
        utterances: true,        // Segment speech into meaningful semantic units
        filler_words: false,     // Remove filler words like "uh" and "um" for cleaner transcripts
      });

      connection.addListener(LiveTranscriptionEvents.Open, () => {
        mediaRecorderRef.current.start(1000);
        setIsRecording(true);
        setDiarizationStatus('detecting');
        setSpeakerCount(0);
      });

      connection.addListener(LiveTranscriptionEvents.Close, () => {
        console.log('Deepgram connection closed');
      });

      connection.addListener(LiveTranscriptionEvents.Transcript, (data) => {
        if (data?.channel?.alternatives?.[0]?.transcript) {
          const alternative = data.channel.alternatives[0];
          const receivedTranscript = alternative.transcript;
          const isInterim = data.is_final === false;
          
          // Debug logging to see what data we're receiving
          console.log('Deepgram Response:', {
            transcript: receivedTranscript,
            isInterim: isInterim,
            isFinal: data.is_final,
            hasWords: !!alternative.words,
            wordCount: alternative.words?.length || 0,
            words: alternative.words?.slice(0, 3), // Show first 3 words for debugging
            hasSpeakerData: alternative.words?.some(word => word.speaker !== undefined),
          });
          
          const timestamp = new Date();
          const speaker = alternative.words?.[0]?.speaker !== undefined ? alternative.words[0].speaker : 0;
          
          if (isInterim) {
            // Handle interim results - show immediately but don't save permanently
            setInterimSegment({
              id: 'interim',
              speaker: speaker,
              text: receivedTranscript,
              timestamp: timestamp,
              type: 'interim'
            });
          } else {
            // Handle final results - save permanently and process
            setInterimSegment(null); // Clear interim result
            
            let formattedTranscript = receivedTranscript;
            
            // Check if we have utterances (recommended by Deepgram docs for diarization)
            if (data.channel?.alternatives?.[0]?.utterances && data.channel.alternatives[0].utterances.length > 0) {
              const utterances = data.channel.alternatives[0].utterances;
              console.log('Using utterances for diarization:', utterances.length);
              
              // Format using utterances (cleaner approach from docs)
              formattedTranscript = utterances
                .map(utterance => `[Speaker ${utterance.speaker}]: ${utterance.transcript}`)
                .join('\n');
                
              const uniqueSpeakers = [...new Set(utterances.map(u => u.speaker))];
              setSpeakerCount(uniqueSpeakers.length);
              setDiarizationStatus(uniqueSpeakers.length > 1 ? 'active' : 'detecting');
              console.log('Diarization working via utterances! Speakers found:', uniqueSpeakers.length);
              
              // Add utterances as separate segments
              const newUtterances = utterances.map(utterance => ({
                id: `${timestamp.getTime()}-${utterance.speaker}`,
                speaker: utterance.speaker,
                text: utterance.transcript,
                timestamp: timestamp,
                type: 'final'
              }));
              
              setTranscriptSegments(prev => [...prev, ...newUtterances]);
              
            } else if (alternative.words && alternative.words.length > 0) {
              // Fallback to word-level diarization
              const hasSpeakers = alternative.words.some(word => word.speaker !== undefined);
              
              if (hasSpeakers) {
                // Group words by speaker and format (following docs format)
                const speakers = {};
                alternative.words.forEach(word => {
                  const speaker = word.speaker !== undefined ? word.speaker : 0;
                  if (!speakers[speaker]) {
                    speakers[speaker] = [];
                  }
                  speakers[speaker].push(word.word);
                });
                
                // Format with speaker labels (using docs format)
                formattedTranscript = Object.entries(speakers)
                  .map(([speaker, words]) => `[Speaker ${speaker}]: ${words.join(' ')}`)
                  .join('\n');
                  
                console.log('Diarization working via words! Speakers found:', Object.keys(speakers).length);
                setSpeakerCount(Object.keys(speakers).length);
                setDiarizationStatus(Object.keys(speakers).length > 1 ? 'active' : 'detecting');
              }
              
              // Add as single segment
              setTranscriptSegments(prev => [...prev, {
                id: `${timestamp.getTime()}-${Math.random()}`,
                speaker: speaker,
                text: receivedTranscript,
                timestamp: timestamp,
                type: 'final'
              }]);
              
            } else {
              console.log('No diarization data found (no utterances or words)');
              
              // Add as single segment without speaker info
              setTranscriptSegments(prev => [...prev, {
                id: `${timestamp.getTime()}-${Math.random()}`,
                speaker: 0,
                text: receivedTranscript,
                timestamp: timestamp,
                type: 'final'
              }]);
            }
            
            // Update the raw transcript for backend processing (only final results)
            setTranscript(prevTranscript => {
              const timestampStr = timestamp.toLocaleTimeString();
              return (prevTranscript + '\n\n' + `[${timestampStr}] ${formattedTranscript}`).trim();
            });
          }
        }
      });

      connection.addListener(LiveTranscriptionEvents.Error, (error) => {
        console.error('Deepgram error:', error);
        toast.error('Transcription error occurred');
      });

      socketRef.current = connection;

    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (socketRef.current) {
        socketRef.current.finish();
      }
      setIsRecording(false);
      
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      
      // Save the meeting
      try {
        setIsProcessing(true);
        const meetingData = {
          title: title.trim(),
          date: new Date().toISOString(),
          transcript: transcript,
          audioUrl: audioUrl
        };

        const response = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
          {
            method: 'POST',
            body: JSON.stringify(meetingData)
          }
        );

        if (!response.ok) {
          throw new Error('Failed to save meeting');
        }

        const savedMeeting = await response.json();
        toast.success('Meeting saved successfully');
        navigate(`/meetings/${savedMeeting.id}`);
      } catch (error) {
        console.error('Error saving meeting:', error);
        toast.error('Failed to save meeting');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-theme theme-transition">
      {/* Header */}
      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm mb-4 overflow-x-auto whitespace-nowrap pb-1">
            <button
              onClick={() => navigate('/meetings')}
              className="text-accent hover:text-theme hover:underline"
            >
              Meetings
            </button>
            <span className="mx-2 text-accent">/</span>
            <span className="text-theme">
              New Meeting
            </span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex-1 max-w-2xl">
              <input
                type="text"
                placeholder="Enter meeting title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-xl sm:text-2xl font-bold px-0 border-0 focus:ring-0 bg-theme text-theme placeholder:text-accent"
              />
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {isRecording ? (
                <button
                  onClick={stopRecording}
                  disabled={isProcessing}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                >
                  <Circle size={20} className="animate-pulse" />
                  {isProcessing ? 'Processing...' : 'Stop Recording'}
                </button>
              ) : (
                <>
                  <button
                    onClick={startRecording}
                    className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600 transition-all"
                  >
                    <Mic size={20} />
                    Start Recording
                  </button>
                  <label className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-secondary text-accent hover:text-theme transition-all cursor-pointer">
                    <Video size={20} />
                    <span className="whitespace-nowrap">Upload Audio</span>
                    <input 
                      type="file" 
                      accept="audio/*" 
                      className="hidden" 
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col max-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-theme">Live Transcript</h2>
            {isRecording && (
              <div className="flex items-center gap-2 text-sm">
                {diarizationStatus === 'waiting' && (
                  <div className="flex items-center gap-2 text-accent">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span>Diarization ready</span>
                  </div>
                )}
                {diarizationStatus === 'detecting' && (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <span>Listening for speakers...</span>
                  </div>
                )}
                {diarizationStatus === 'active' && speakerCount > 1 && (
                  <div className="flex items-center gap-2 text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>{speakerCount} speakers detected</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 bg-secondary rounded-lg p-4 overflow-y-auto">
            {(transcriptSegments.length > 0 || interimSegment) ? (
              <div className="space-y-4">
                {/* Final transcript segments */}
                {transcriptSegments.map((segment) => (
                  <div key={segment.id} className="flex gap-3 group">
                    <div className="flex flex-col items-center min-w-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSpeakerColor(segment.speaker)}`}>
                        Speaker {segment.speaker}
                      </span>
                      <span className="text-xs text-accent mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {formatRelativeTime(segment.timestamp)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-theme leading-relaxed">
                        {segment.text}
                      </p>
                    </div>
                  </div>
                ))}
                
                {/* Interim result - shown in real-time */}
                {interimSegment && (
                  <div className="flex gap-3 group animate-pulse">
                    <div className="flex flex-col items-center min-w-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSpeakerColor(interimSegment.speaker)} opacity-70`}>
                        Speaker {interimSegment.speaker}
                      </span>
                      <span className="text-xs text-accent mt-1 opacity-50">
                        typing...
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-theme leading-relaxed italic opacity-70">
                        {interimSegment.text}
                      </p>
                    </div>
                  </div>
                )}
                
                {isRecording && (
                  <div className="flex items-center gap-2 text-accent text-sm">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span>Recording...</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-accent">
                <Video size={48} className="mb-4" />
                <p className="text-center">
                  {isRecording 
                    ? 'Listening...' 
                    : 'Start recording to see the live transcript'}
                </p>
                {isRecording && (
                  <p className="text-sm text-accent mt-2">
                    Diarization will identify speakers automatically
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRecorder; 